import { useState, useEffect } from "react";
import { Download, FileCode, FolderOpen, Chrome, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FILE_ICONS = {
  ".json": "📋",
  ".js":   "⚙️",
  ".html": "🌐",
  ".css":  "🎨",
  ".png":  "🖼️",
};

function fileIcon(name) {
  const ext = name.slice(name.lastIndexOf("."));
  return FILE_ICONS[ext] || "📄";
}

function fmt(bytes) {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

const STEPS = [
  "Download the zip below (or individual files)",
  "Unzip it — you'll get an extension/ folder",
  'Go to chrome://extensions in Chrome',
  "Enable Developer Mode (toggle, top-right)",
  'Click "Load unpacked" and select the extension/ folder',
  "The Coral icon appears in your toolbar — you're done!",
];

export default function ExtensionDownloadPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/extension/files`)
      .then(r => r.json())
      .then(d => setFiles(d.files || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAFA", fontFamily: "Inter, sans-serif" }}>
      {/* Nav */}
      <nav style={{
        background: "#fff", borderBottom: "1px solid #E8E8E8",
        padding: "0 24px", height: "56px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img src="/coral-icon.webp" width="30" height="30" style={{ borderRadius: "8px" }} alt="Coral" />
          <span style={{ fontFamily: "Manrope", fontWeight: 800, fontSize: "17px" }}>coral</span>
        </div>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#666", textDecoration: "none" }}>
          <ArrowLeft size={14} /> Back to demo
        </Link>
      </nav>

      <div style={{ maxWidth: "680px", margin: "0 auto", padding: "40px 24px" }}>
        <h1 style={{ fontFamily: "Manrope", fontSize: "28px", fontWeight: 800, marginBottom: "8px" }}>
          Get the Extension
        </h1>
        <p style={{ fontSize: "14px", color: "#666", marginBottom: "32px", lineHeight: 1.6 }}>
          Install Coral as an unpacked Chrome extension in under a minute.
        </p>

        {/* Primary download */}
        <div style={{
          background: "#FFF8F7", border: "1px solid #FAD7D4",
          borderRadius: "12px", padding: "20px 24px", marginBottom: "24px",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <div style={{ fontFamily: "Manrope", fontSize: "15px", fontWeight: 700, marginBottom: "4px" }}>
                coral-extension.zip
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                All extension files — unzip and load in Chrome
              </div>
            </div>
            <a
              href={`${API}/extension/download`}
              download="coral-extension.zip"
              data-testid="download-zip-btn"
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                background: "#FA7268", color: "#fff",
                padding: "10px 18px", borderRadius: "8px",
                fontSize: "13px", fontWeight: 600, textDecoration: "none",
                fontFamily: "Manrope",
              }}
            >
              <Download size={14} /> Download .zip
            </a>
          </div>
        </div>

        {/* Install steps */}
        <div style={{ background: "#fff", border: "1px solid #E8E8E8", borderRadius: "12px", padding: "20px 24px", marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
            <Chrome size={16} color="#FA7268" />
            <span style={{ fontFamily: "Manrope", fontSize: "14px", fontWeight: 700 }}>Install steps</span>
          </div>
          {STEPS.map((step, i) => (
            <div key={i} style={{ display: "flex", gap: "12px", marginBottom: "10px" }}>
              <div style={{
                width: "22px", height: "22px", borderRadius: "50%",
                background: "#FFF8F7", border: "1.5px solid #FA7268",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "11px", fontWeight: 700, color: "#FA7268", flexShrink: 0,
              }}>{i + 1}</div>
              <span style={{ fontSize: "13px", color: "#444", lineHeight: 1.5, paddingTop: "2px" }}>
                {step.includes("chrome://extensions") ? (
                  <>{step.split("chrome://extensions")[0]}
                    <code style={{ background: "#F5F5F5", padding: "1px 5px", borderRadius: "4px", fontSize: "12px", fontFamily: "monospace" }}>
                      chrome://extensions
                    </code>
                    {step.split("chrome://extensions")[1]}
                  </>
                ) : step}
              </span>
            </div>
          ))}
        </div>

        {/* Individual files fallback */}
        <div style={{ background: "#fff", border: "1px solid #E8E8E8", borderRadius: "12px", padding: "20px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <FolderOpen size={16} color="#666" />
            <span style={{ fontFamily: "Manrope", fontSize: "14px", fontWeight: 700 }}>
              Download individual files
            </span>
          </div>
          <p style={{ fontSize: "12px", color: "#999", marginBottom: "14px" }}>
            If the zip doesn't work, download each file below and recreate the folder structure manually.
          </p>

          {loading ? (
            <p style={{ fontSize: "12px", color: "#999" }}>Loading file list...</p>
          ) : (
            <div style={{ display: "grid", gap: "1px" }}>
              {/* Group by folder */}
              {["", "icons"].map(folder => {
                const folderFiles = files.filter(f =>
                  folder === "" ? !f.path.includes("/") : f.path.startsWith(folder + "/")
                );
                if (!folderFiles.length) return null;
                return (
                  <div key={folder || "root"} style={{ marginBottom: "12px" }}>
                    {folder && (
                      <div style={{ fontSize: "11px", fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px", marginLeft: "4px" }}>
                        📁 {folder}/
                      </div>
                    )}
                    {folderFiles.map(f => (
                      <div key={f.path} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "7px 10px",
                        borderRadius: "6px",
                        border: "1px solid #F0F0F0",
                        marginBottom: "4px",
                        background: "#FAFAFA",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ fontSize: "14px" }}>{fileIcon(f.name)}</span>
                          <span style={{ fontSize: "12px", fontWeight: 500, fontFamily: "monospace" }}>{f.name}</span>
                          <span style={{ fontSize: "11px", color: "#bbb" }}>{fmt(f.size)}</span>
                        </div>
                        <a
                          href={`${API}/extension/file/${f.path}`}
                          download={f.name}
                          style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#FA7268", fontWeight: 600, textDecoration: "none" }}
                        >
                          <Download size={11} /> Save
                        </a>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: "14px", padding: "10px 12px", background: "#F9F9F9", borderRadius: "8px" }}>
            <p style={{ fontSize: "11px", color: "#888", lineHeight: 1.5 }}>
              <strong>Folder structure:</strong> Save all root files in one folder named <code style={{ fontFamily: "monospace", background: "#eee", padding: "1px 4px", borderRadius: "3px" }}>extension/</code>, and the icon files in <code style={{ fontFamily: "monospace", background: "#eee", padding: "1px 4px", borderRadius: "3px" }}>extension/icons/</code>. Then load the <code style={{ fontFamily: "monospace", background: "#eee", padding: "1px 4px", borderRadius: "3px" }}>extension/</code> folder in Chrome.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
